import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from './useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

/**
 * Initialize GSAP ScrollTrigger animations
 * Respects prefers-reduced-motion
 */
export const useScrollAnimations = () => {
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      // Disable all animations if user prefers reduced motion
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      return;
    }

    // Fade in sections on scroll
    const sections = gsap.utils.toArray<HTMLElement>('.animate-section');
    sections
      .filter((section): section is HTMLElement => Boolean(section))
      .forEach((section) => {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 85%',
              end: 'top 60%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });

    // Stagger cards
    const cardGroups = gsap.utils.toArray<HTMLElement>('.card-group');
    cardGroups
      .filter((group): group is HTMLElement => Boolean(group))
      .forEach((group) => {
        const cards = Array.from(group.querySelectorAll<HTMLElement>('.card'));
        if (!cards.length) {
          return;
        }

        gsap.fromTo(
          cards,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: group,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [prefersReducedMotion]);
};
